/**
 * Native Platform Utilities for Capacitor
 * Detects platform and provides native API wrappers with web fallbacks
 */

import { Capacitor, registerPlugin } from '@capacitor/core';
// Static import: creating the plugin proxy is safe on every platform —
// the "Browser.then() is not implemented" crash only happens when the
// proxy itself is awaited/returned from an async function (thenable trap).
import { Browser } from '@capacitor/browser';
// Static import of App as well: needed to register the `appUrlOpen` deep
// link listener at module load, before any Google login can happen.
import { App } from '@capacitor/app';

// ============================================================
// PLATFORM DETECTION
// ============================================================

// Check if running on a native platform. We INTENTIONALLY do NOT check for
// any specific plugin (e.g. '@capacitor/app') availability here — that was
// the previous heuristic, but on Swift Package Manager iOS projects where
// individual plugins may not be registered yet at module-eval time it
// caused `isNative` to be falsely `false`. That in turn made every
// `!isNative` branch in the codebase (Camminata, etc.) fall through to
// `navigator.geolocation`, which is what triggers the WKWebView origin
// permission prompt ("slimmy.app would like to use your current location")
// and prevented the iOS "Consenti sempre" upgrade prompt from ever firing.
//
// If we are running inside a Capacitor app shell, that is the source of
// truth for "native"; downstream wrappers can each do their own
// per-plugin availability checks (see `getBgGeoPlugin`, `getGeo`, etc.).
const checkNative = () => {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

export const isNative = checkNative();
export const platform = Capacitor.getPlatform(); // 'ios' | 'android' | 'web'
export const isIOS = platform === 'ios';
export const isAndroid = platform === 'android';
export const isWeb = platform === 'web' || !isNative;

console.log('[Native] Platform:', platform, 'isNative:', isNative, 'pluginsAvailable:', Capacitor.isPluginAvailable('App'));

// ============================================================
// HAPTICS
// ============================================================

let HapticsPlugin = null;

const getHaptics = async () => {
  if (!isNative) return null;
  if (!HapticsPlugin) {
    const { Haptics } = await import('@capacitor/haptics');
    HapticsPlugin = Haptics;
  }
  return HapticsPlugin;
};

export const haptics = {
  impact: async (style = 'Medium') => {
    const h = await getHaptics();
    if (h) {
      await h.impact({ style }).catch(() => {});
    } else if (navigator.vibrate) {
      const ms = style === 'Light' ? 10 : style === 'Heavy' ? 80 : 40;
      navigator.vibrate(ms);
    }
  },
  notification: async (type = 'Success') => {
    const h = await getHaptics();
    if (h) {
      await h.notification({ type }).catch(() => {});
    } else if (navigator.vibrate) {
      navigator.vibrate([50, 100, 50]);
    }
  },
  selectionStart: async () => {
    const h = await getHaptics();
    if (h) await h.selectionStart().catch(() => {});
  },
  selectionChanged: async () => {
    const h = await getHaptics();
    if (h) await h.selectionChanged().catch(() => {});
  },
  selectionEnd: async () => {
    const h = await getHaptics();
    if (h) await h.selectionEnd().catch(() => {});
  },
};

// ============================================================
// CAMERA
// ============================================================

let CameraPlugin = null;

const getCamera = async () => {
  if (!CameraPlugin) {
    const { Camera } = await import('@capacitor/camera');
    CameraPlugin = Camera;
  }
  return CameraPlugin;
};

/**
 * Take a photo using native camera or file picker
 * @returns {Promise<{dataUrl: string, blob: Blob, file: File}>}
 */
export const takePhoto = async (options = {}) => {
  // IMPORTANT: re-check native at CALL TIME, not module-load. On Android
  // the Capacitor bridge isn't always ready at module evaluation, so the
  // exported `isNative` constant can be stuck at false even on native.
  const nativeNow = (() => { try { return Capacitor.isNativePlatform(); } catch { return false; } })();
  console.log('[Camera] takePhoto called, nativeNow:', nativeNow, 'platform:', Capacitor.getPlatform());
  if (!nativeNow) {
    console.log('[Camera] Not native, returning null');
    return null; // fallback to HTML input
  }

  try {
    const camera = await getCamera();
    const { CameraResultType, CameraSource } = await import('@capacitor/camera');

    // CRITICAL: explicitly request permissions on Android before getPhoto.
    // Without this, Camera.getPhoto() can silently fail on Android 13+
    // when the runtime permission prompt is needed.
    try {
      const perms = await camera.checkPermissions();
      console.log('[Camera] Current permissions:', perms);
      if (perms.camera !== 'granted' || perms.photos !== 'granted') {
        const req = await camera.requestPermissions({ permissions: ['camera', 'photos'] });
        console.log('[Camera] Requested permissions result:', req);
        if (req.camera === 'denied' && req.photos === 'denied') {
          throw new Error('Camera/Photos permission denied');
        }
      }
    } catch (permErr) {
      console.warn('[Camera] Permission check/request error:', permErr?.message || permErr);
      // continue anyway; getPhoto will surface a clearer error if blocked
    }

    const srcMap = {
      gallery: CameraSource.Photos,
      camera: CameraSource.Camera,
      prompt: CameraSource.Prompt,
    };
    const src = srcMap[options.source] || CameraSource.Camera;
    console.log('[Camera] Opening camera with source:', options.source || 'camera');
    const photo = await camera.getPhoto({
      quality: options.quality || 85,
      allowEditing: options.allowEditing || false,
      resultType: CameraResultType.DataUrl,
      source: src,
      width: options.width || 1200,
      height: options.height || 1200,
      correctOrientation: true,
      // presentationStyle is iOS-only; on Android it's harmless but we
      // omit promptLabel* which are also iOS-only Prompt UI labels.
    });

    console.log('[Camera] Photo taken successfully, format:', photo.format, 'dataUrl length:', photo.dataUrl?.length || 0);
    // Convert dataUrl to blob/file
    const response = await fetch(photo.dataUrl);
    const blob = await response.blob();
    const file = new File([blob], `photo_${Date.now()}.${photo.format || 'jpeg'}`, {
      type: `image/${photo.format || 'jpeg'}`,
    });

    return { dataUrl: photo.dataUrl, blob, file };
  } catch (e) {
    console.log('[Camera] Error or cancelled:', e.message);
    throw e;
  }
};

/**
 * Pick photo from gallery
 */
export const pickFromGallery = async (options = {}) => {
  return takePhoto({ ...options, source: 'gallery' });
};

/**
 * Pick MULTIPLE photos from the system gallery in a single sweep.
 *
 * On iOS/Android native this uses Capacitor's `Camera.pickImages()`
 * which opens the native Photos library DIRECTLY — no intermediate
 * WKWebView prompt with "Photo Library / Take Photo / Choose Files"
 * (which is what `<input type="file">` triggers on iOS).
 *
 * Returns `null` on web — the caller is expected to fall back to a
 * regular `<input type="file" multiple>` element in that case.
 *
 * @param {object} options
 * @param {number} [options.limit=3] max number of images
 * @returns {Promise<Array<{dataUrl: string, blob: Blob, file: File}> | null>}
 */
export const pickMultipleFromGallery = async (options = {}) => {
  // Runtime native check (vedi commento in takePhoto): la costante
  // `isNative` valutata a module-load può essere falsamente `false`
  // su iOS quando la bridge Capacitor non è ancora pronta.
  const nativeNow = (() => { try { return Capacitor.isNativePlatform(); } catch { return false; } })();
  console.log('[pickMultipleFromGallery] nativeNow:', nativeNow);
  if (!nativeNow) return null;
  try {
    const camera = await getCamera();
    const result = await camera.pickImages({
      quality: options.quality ?? 85,
      limit: Math.max(1, Math.min(10, options.limit ?? 3)),
      correctOrientation: true,
      // presentationStyle is honoured on iOS only.
      presentationStyle: 'fullScreen',
    });

    const photos = (result?.photos || []);
    if (!photos.length) return [];

    // Materialise each native file:// path (iOS) or content:// URI
    // (Android) into a DataURL + Blob + File, so the rest of the
    // existing post-processing pipeline (EXIF orientation, compression,
    // base64 upload) keeps working unchanged.
    const out = [];
    for (const p of photos) {
      try {
        const url = p?.webPath || p?.path;
        if (!url) continue;
        const response = await fetch(url);
        const blob = await response.blob();
        const dataUrl = await new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(r.result);
          r.onerror = () => rej(new Error('readAsDataURL failed'));
          r.readAsDataURL(blob);
        });
        const file = new File(
          [blob],
          `photo_${Date.now()}_${out.length}.${(p?.format || 'jpeg')}`,
          { type: blob.type || `image/${p?.format || 'jpeg'}` },
        );
        out.push({ dataUrl, blob, file });
      } catch (inner) {
        console.warn('[pickMultipleFromGallery] skipping one photo:', inner?.message);
      }
    }
    return out;
  } catch (e) {
    console.log('[pickMultipleFromGallery] error or cancelled:', e?.message);
    throw e;
  }
};

// ============================================================
// SHARE
// ============================================================

let SharePlugin = null;

const getShare = async () => {
  if (!SharePlugin) {
    const { Share } = await import('@capacitor/share');
    SharePlugin = Share;
  }
  return SharePlugin;
};

/**
 * Share content using native share sheet
 */
export const shareContent = async ({ title, text, url, dialogTitle }) => {
  if (isNative) {
    const share = await getShare();
    return share.share({ title, text, url, dialogTitle: dialogTitle || title });
  }
  // Web fallback
  if (navigator.share) {
    return navigator.share({ title, text, url });
  }
  // Last resort: copy to clipboard
  if (url) {
    await navigator.clipboard?.writeText(url);
  }
  return null;
};

// ============================================================
// KEYBOARD
// ============================================================

let KeyboardPlugin = null;

const getKeyboard = async () => {
  if (!isNative) return null;
  if (!KeyboardPlugin) {
    const { Keyboard } = await import('@capacitor/keyboard');
    KeyboardPlugin = Keyboard;
  }
  return KeyboardPlugin;
};

export const keyboard = {
  // iOS only: disable WKWebView native scroll (prevents fixed headers
  // being pushed off-screen when the keyboard opens)
  setScrollDisabled: async (isDisabled) => {
    if (!isIOS) return;
    const k = await getKeyboard();
    if (k) await k.setScroll({ isDisabled }).catch(() => {});
  },
  hide: async () => {
    const k = await getKeyboard();
    if (k) await k.hide().catch(() => {});
  },
  show: async () => {
    const k = await getKeyboard();
    if (k) await k.show().catch(() => {});
  },
  onShow: async (callback) => {
    const k = await getKeyboard();
    if (k) return k.addListener('keyboardWillShow', callback);
    return null;
  },
  onHide: async (callback) => {
    const k = await getKeyboard();
    if (k) return k.addListener('keyboardWillHide', callback);
    return null;
  },
};

// ============================================================
// NETWORK
// ============================================================

let NetworkPlugin = null;

const getNetwork = async () => {
  if (!isNative) return null;
  if (!NetworkPlugin) {
    try {
      const { Network } = await import('@capacitor/network');
      NetworkPlugin = Network;
    } catch {
      return null;
    }
  }
  return NetworkPlugin;
};

export const network = {
  getStatus: async () => {
    const n = await getNetwork();
    if (n) return n.getStatus();
    return { connected: navigator.onLine, connectionType: 'unknown' };
  },
  onChange: async (callback) => {
    const n = await getNetwork();
    if (n) return n.addListener('networkStatusChange', callback);
    // Web fallback
    const handler = () => callback({ connected: navigator.onLine, connectionType: 'unknown' });
    window.addEventListener('online', handler);
    window.addEventListener('offline', handler);
    return { remove: () => {
      window.removeEventListener('online', handler);
      window.removeEventListener('offline', handler);
    }};
  },
};

// ============================================================
// KEEP AWAKE (Screen stays on during walks)
// ============================================================

let KeepAwakePlugin = null;

const getKeepAwake = async () => {
  if (!KeepAwakePlugin) {
    try {
      const { KeepAwake } = await import('@capacitor-community/keep-awake');
      KeepAwakePlugin = KeepAwake;
    } catch {
      return null;
    }
  }
  return KeepAwakePlugin;
};

export const keepAwake = {
  enable: async () => {
    // On native: use Capacitor KeepAwake plugin
    if (isNative) {
      try {
        const ka = await getKeepAwake();
        if (ka) { await ka.keepAwake(); return true; }
      } catch { /* plugin not available */ }
    }
    // Web fallback: Wake Lock API
    if ('wakeLock' in navigator) {
      try {
        const lock = await navigator.wakeLock.request('screen');
        window.__wakeLock = lock;
        return true;
      } catch { return false; }
    }
    return false;
  },
  disable: async () => {
    // On native: use Capacitor KeepAwake plugin
    if (isNative) {
      try {
        const ka = await getKeepAwake();
        if (ka) { await ka.allowSleep(); return; }
      } catch { /* plugin not available */ }
    }
    // Web fallback
    if (window.__wakeLock) {
      try { await window.__wakeLock.release(); } catch { /* ignore */ }
      window.__wakeLock = null;
    }
  },
};

// ============================================================
// BIOMETRIC AUTH (Face ID / Touch ID / Fingerprint)
// ============================================================

let BiometricPlugin = null;
// Cache: once the bridge has shouted "not implemented on ios" (which
// happens when the JS package is installed but the native pod isn't
// linked), we DON'T retry on subsequent calls. Otherwise every page
// that polls biometry status fires the same error and floods the
// console — and on iOS WKWebView it can also break unrelated promise
// chains because the error bubbles up unhandled.
let biometricDisabled = false;

const getBiometric = async () => {
  if (!isNative) return null;
  if (biometricDisabled) return null;
  // Capacitor.isPluginAvailable on iOS returns true even when ONLY
  // the JS package is present (the native registration check is
  // sometimes misleading right after `cap sync`). So we treat this
  // as a hint, not as a guarantee — the real proof is the first
  // bridge call below.
  try {
    if (Capacitor?.isPluginAvailable && !Capacitor.isPluginAvailable('NativeBiometric')) {
      biometricDisabled = true;
      return null;
    }
  } catch (_) { /* if isPluginAvailable itself throws, continue and let import try */ }
  if (!BiometricPlugin) {
    try {
      const { NativeBiometric } = await import('@capgo/capacitor-native-biometric');
      BiometricPlugin = NativeBiometric;
    } catch (e) {
      console.log('[Biometric] Plugin import failed:', e.message);
      biometricDisabled = true;
      return null;
    }
  }
  return BiometricPlugin;
};

// Wraps a plugin call so any "not implemented on ios" / native bridge
// error gets caught ONCE and disables the plugin for the rest of the
// session — no UI alert, no unhandled promise rejection.
const safeBioCall = async (fn, fallback) => {
  try {
    return await fn();
  } catch (e) {
    const msg = String(e?.message || e || '');
    if (msg.includes('not implemented') || msg.includes('unimplemented')) {
      biometricDisabled = true;
    }
    return fallback;
  }
};

export const biometric = {
  /**
   * Check if biometric auth is available
   * @returns {{ isAvailable: boolean, biometryType: string }}
   */
  isAvailable: async () => {
    const bio = await getBiometric();
    if (!bio) return { isAvailable: false, biometryType: 'none' };
    return safeBioCall(async () => {
      const result = await bio.isAvailable();
      return {
        isAvailable: result.isAvailable,
        biometryType: result.biometryType === 1 ? 'touchId' :
                      result.biometryType === 2 ? 'faceId' :
                      result.biometryType === 3 ? 'fingerprint' :
                      result.biometryType === 4 ? 'faceAuth' : 'none',
      };
    }, { isAvailable: false, biometryType: 'none' });
  },

  /**
   * Authenticate with biometrics
   */
  authenticate: async (reason = 'Accedi a Slimmy') => {
    const bio = await getBiometric();
    if (!bio) return false;
    return safeBioCall(async () => {
      await bio.verifyIdentity({
        reason,
        title: 'Slimmy',
        subtitle: 'Accesso rapido',
        description: reason,
      });
      return true;
    }, false);
  },

  /**
   * Save credentials securely in the device keychain
   */
  saveCredentials: async (username, password) => {
    const bio = await getBiometric();
    if (!bio) return false;
    return safeBioCall(async () => {
      await bio.setCredentials({
        username,
        password,
        server: 'slimmy.app',
      });
      return true;
    }, false);
  },

  /**
   * Get saved credentials
   */
  getCredentials: async () => {
    const bio = await getBiometric();
    if (!bio) return null;
    return safeBioCall(async () => {
      const creds = await bio.getCredentials({ server: 'slimmy.app' });
      return creds;
    }, null);
  },

  /**
   * Delete saved credentials
   */
  deleteCredentials: async () => {
    const bio = await getBiometric();
    if (!bio) return;
    await safeBioCall(async () => {
      await bio.deleteCredentials({ server: 'slimmy.app' });
    }, undefined);
  },
};

// ============================================================
// PUSH NOTIFICATIONS (Native)
// ============================================================

let PushPlugin = null;

const getPush = async () => {
  if (!isNative) return null;
  if (!PushPlugin) {
    try {
      // webpackIgnore so the bundler doesn't try to resolve this package
      // at build time on environments where it's not installed (e.g. the
      // preview Pod). The Capacitor runtime loads it natively on device.
      const mod = await import(/* webpackIgnore: true */ '@capacitor/push-notifications');
      PushPlugin = mod?.PushNotifications || null;
    } catch {
      return null;
    }
  }
  return PushPlugin;
};

export const pushNotifications = {
  /**
   * Request permission and register for push notifications
   */
  register: async () => {
    const push = await getPush();
    if (!push) return null;

    const permission = await push.requestPermissions();
    if (permission.receive === 'granted') {
      await push.register();
      return true;
    }
    return false;
  },

  /**
   * Listen for registration token
   */
  onRegistration: async (callback) => {
    const push = await getPush();
    if (!push) return null;
    return push.addListener('registration', callback);
  },

  /**
   * Listen for push notification received while app is open
   */
  onReceived: async (callback) => {
    const push = await getPush();
    if (!push) return null;
    return push.addListener('pushNotificationReceived', callback);
  },

  /**
   * Listen for notification tap action
   */
  onActionPerformed: async (callback) => {
    const push = await getPush();
    if (!push) return null;
    return push.addListener('pushNotificationActionPerformed', callback);
  },

  /**
   * Get delivered notifications
   */
  getDelivered: async () => {
    const push = await getPush();
    if (!push) return { notifications: [] };
    return push.getDeliveredNotifications();
  },

  /**
   * Remove all delivered notifications
   */
  removeAll: async () => {
    const push = await getPush();
    if (!push) return;
    await push.removeAllDeliveredNotifications();
  },
};

// ============================================================
// LOCAL NOTIFICATIONS (Badge count)
// ============================================================

let LocalNotifPlugin = null;

const getLocalNotif = async () => {
  if (!LocalNotifPlugin) {
    try {
      const { LocalNotifications } = await import('@capacitor/local-notifications');
      LocalNotifPlugin = LocalNotifications;
    } catch {
      return null;
    }
  }
  return LocalNotifPlugin;
};

export const badge = {
  set: async (count) => {
    if (isNative) {
      const ln = await getLocalNotif();
      if (ln) {
        // Use native badge API
        try {
          // On iOS, setting badge directly
          if (isIOS) {
            // iOS handles badge via push notification payload or local notification
          }
        } catch (_) { /* swallow */ }
      }
    }
    // Web fallback
    if ('setAppBadge' in navigator) {
      navigator.setAppBadge(count).catch(() => {});
    }
  },
  clear: async () => {
    if ('clearAppBadge' in navigator) {
      navigator.clearAppBadge().catch(() => {});
    }
  },
};

// ============================================================
// STATUS BAR
// ============================================================

let StatusBarPlugin = null;

const getStatusBar = async () => {
  if (!isNative) return null;
  if (!StatusBarPlugin) {
    const { StatusBar } = await import('@capacitor/status-bar');
    StatusBarPlugin = StatusBar;
  }
  return StatusBarPlugin;
};

export const statusBar = {
  setDark: async () => {
    const sb = await getStatusBar();
    if (sb) {
      const { Style } = await import('@capacitor/status-bar');
      await sb.setStyle({ style: Style.Dark }).catch(() => {});
      await sb.setBackgroundColor({ color: '#080D1A' }).catch(() => {});
    }
  },
  setLight: async () => {
    const sb = await getStatusBar();
    if (sb) {
      const { Style } = await import('@capacitor/status-bar');
      await sb.setStyle({ style: Style.Light }).catch(() => {});
      await sb.setBackgroundColor({ color: '#FFFFFF' }).catch(() => {});
    }
  },
  hide: async () => {
    const sb = await getStatusBar();
    if (sb) await sb.hide().catch(() => {});
  },
  show: async () => {
    const sb = await getStatusBar();
    if (sb) await sb.show().catch(() => {});
  },
};

// ============================================================
// SPLASH SCREEN
// ============================================================

let SplashPlugin = null;

const getSplash = async () => {
  if (!isNative) return null;
  if (!SplashPlugin) {
    const { SplashScreen } = await import('@capacitor/splash-screen');
    SplashPlugin = SplashScreen;
  }
  return SplashPlugin;
};

export const splash = {
  hide: async () => {
    const s = await getSplash();
    if (s) await s.hide({ fadeOutDuration: 300 }).catch(() => {});
  },
  show: async () => {
    const s = await getSplash();
    if (s) await s.show().catch(() => {});
  },
};

// ============================================================
// APP LIFECYCLE
// ============================================================

// IMPORTANT: never return or await the plugin proxy itself from an async
// function — resolving a Promise with the Capacitor proxy triggers its
// `.then()`, which throws `"App.then()" is not implemented`. `App` is
// statically imported at the top of this file and used directly.
export const appLifecycle = {
  onStateChange: async (callback) => {
    if (isNative) return App.addListener('appStateChange', callback);
    // Web fallback
    const handler = () => callback({ isActive: !document.hidden });
    document.addEventListener('visibilitychange', handler);
    return { remove: () => document.removeEventListener('visibilitychange', handler) };
  },
  onBackButton: async (callback) => {
    if (isNative) return App.addListener('backButton', callback);
    return null;
  },
  onUrlOpen: async (callback) => {
    if (isNative) return App.addListener('appUrlOpen', callback);
    return null;
  },
};

// ============================================================
// GEOLOCATION (Native, more accurate)
// ============================================================

let GeoPlugin = null;

const getGeo = async () => {
  if (!GeoPlugin) {
    const { Geolocation } = await import('@capacitor/geolocation');
    GeoPlugin = Geolocation;
  }
  return GeoPlugin;
};

export const geolocation = {
  getCurrentPosition: async (options = {}) => {
    if (isNative) {
      const geo = await getGeo();
      return geo.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 10000,
        ...options,
      });
    }
    // Web fallback
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 10000,
        ...options,
      });
    });
  },
  watchPosition: async (options = {}, callback) => {
    if (isNative) {
      const geo = await getGeo();
      return geo.watchPosition({
        enableHighAccuracy: true,
        ...options,
      }, callback);
    }
    // Web fallback
    const id = navigator.geolocation.watchPosition(
      (pos) => callback(pos, null),
      (err) => callback(null, err),
      { enableHighAccuracy: true, ...options }
    );
    return id.toString();
  },
  clearWatch: async (watchId) => {
    if (isNative) {
      const geo = await getGeo();
      await geo.clearWatch({ id: watchId });
    } else {
      navigator.geolocation.clearWatch(parseInt(watchId));
    }
  },
};

// ============================================================
// BACKGROUND GEOLOCATION (@capgo/background-geolocation)
//
// Why this exists alongside the regular `geolocation` wrapper:
//   `@capacitor/geolocation` watchPosition() is paused by iOS as
//   soon as the app goes to background OR the screen locks — even
//   with `UIBackgroundModes=location` in Info.plist. It also only
//   ever requests "When in Use" permission, never "Always".
//
//   `@capgo/background-geolocation` keeps the GPS stream alive
//   while the app is backgrounded / screen off / device locked,
//   and asks iOS for the "Always" permission upgrade automatically
//   the first time the app is sent to background while tracking.
//
// Used by Camminata.js when tracking a walk. On web/PWA we fall
// back to `navigator.geolocation` (single foreground stream).
// ============================================================

let BgGeoPlugin = null;
let bgGeoDisabled = false;

const getBgGeo = async () => {
  if (!isNative) return null;
  if (bgGeoDisabled) return null;
  try {
    if (Capacitor?.isPluginAvailable && !Capacitor.isPluginAvailable('BackgroundGeolocation')) {
      bgGeoDisabled = true;
      return null;
    }
  } catch (_) { /* fall through to registerPlugin */ }
  if (!BgGeoPlugin) {
    try {
      // Use registerPlugin — does NOT require @capgo/background-geolocation
      // to be in the JS bundle. The Capacitor bridge resolves the native
      // implementation registered by the iOS Pod / Android module. This
      // works because the JS package is only TypeScript types; the actual
      // implementation lives in the native pod (already in the Podfile).
      BgGeoPlugin = registerPlugin('BackgroundGeolocation');
    } catch (e) {
      console.log('[BgGeo] registerPlugin failed:', e.message);
      bgGeoDisabled = true;
      return null;
    }
  }
  return BgGeoPlugin;
};

const safeBgCall = async (fn, fallback) => {
  try { return await fn(); }
  catch (e) {
    const msg = String(e?.message || e || '');
    if (msg.includes('not implemented') || msg.includes('unimplemented')) {
      bgGeoDisabled = true;
    }
    console.log('[BgGeo] call failed:', msg);
    return fallback;
  }
};

export const backgroundGeolocation = {
  /** True only when the native plugin is actually usable on this device. */
  isAvailable: async () => {
    const p = await getBgGeo();
    return !!p;
  },

  /**
   * Start a background-capable GPS watcher.
   *
   * @param {object} options
   *   - backgroundTitle (string, iOS+Android): notification title
   *   - backgroundMessage (string, iOS+Android): notification body — REQUIRED
   *     to keep updates flowing when the app is backgrounded
   *   - distanceFilter (number, meters): minimum movement before a new fix
   *   - stale (bool): allow possibly-stale fixes for faster first response
   * @param {(position|null, err|null) => void} callback receives every fix or error
   * @returns {boolean} true if the watcher was started, false otherwise
   *
   * NOTE: the @capgo plugin's start() promise only resolves when stop()
   * is called — we therefore DO NOT await it. We fire-and-forget and
   * attach .catch() to swallow the rejection that fires on stop().
   */
  start: async (options, callback) => {
    const p = await getBgGeo();
    if (!p) {
      console.log('[BgGeo] start: plugin unavailable');
      return false;
    }
    try {
      // Defensive: stop any prior session that might be lingering from
      // another page (e.g. the Diagnostica live tracker). Without this
      // a leaked watcher can swallow our fixes or make the second start
      // a no-op on some plugin versions. Safe to call even if no
      // watcher is active.
      try { await p.stop(); } catch (_) { /* expected on first start */ }

      console.log('[BgGeo] start: invoking native plugin with options', options);
      p.start(
        {
          backgroundTitle: 'Slimmy',
          backgroundMessage: 'Tracciamento del movimento in corso',
          requestPermissions: true,
          stale: false,
          distanceFilter: 5,
          ...options,
        },
        (location, error) => {
          if (error) { callback(null, error); return; }
          if (!location) return;
          // Adapt @capgo schema → navigator.geolocation-style position
          const position = {
            coords: {
              latitude: location.latitude,
              longitude: location.longitude,
              accuracy: location.accuracy,
              altitude: location.altitude ?? null,
              altitudeAccuracy: location.altitudeAccuracy ?? null,
              heading: location.bearing ?? null,
              speed: location.speed ?? null,
            },
            timestamp: location.time || Date.now(),
          };
          callback(position, null);
        }
      ).catch((e) => {
        const msg = String(e?.message || e || '');
        // "stopped" / "cancelled" rejection is EXPECTED when stop() fires.
        // Only flag the plugin as disabled for true "not implemented" errors.
        if (msg.includes('not implemented') || msg.includes('unimplemented')) {
          console.log('[BgGeo] start rejected as not implemented — disabling');
          bgGeoDisabled = true;
        } else {
          console.log('[BgGeo] start promise settled:', msg);
        }
      });
      return true;
    } catch (e) {
      const msg = String(e?.message || e || '');
      if (msg.includes('not implemented')) bgGeoDisabled = true;
      console.log('[BgGeo] start sync error:', msg);
      return false;
    }
  },

  /** Stop the watcher. Always safe to call. */
  stop: async () => {
    const p = await getBgGeo();
    if (!p) return;
    try { await p.stop(); } catch (_) { /* ignore */ }
  },

  /** Open the OS settings page when the user denied "Always" permission. */
  openSettings: async () => {
    const p = await getBgGeo();
    if (!p) return;
    try { await p.openSettings(); } catch (_) { /* ignore */ }
  },
};



// ============================================================
// BROWSER (Open external links)
// ============================================================

// Static import of `Browser` is at the top of this file (thenable trap fix).

export const browser = {
  open: async (url) => {
    if (isNative) {
      await Browser.open({
        url,
        presentationStyle: 'fullscreen',
        toolbarColor: '#080D1A',
        windowName: '_blank'
      });
    } else {
      window.open(url, '_blank');
    }
  },
  close: async () => {
    if (isNative) {
      await Browser.close().catch(() => {});
    }
  }
};

// ============================================================
// DEEP LINKS (OAuth return + universal links)
// ============================================================

// Shared handler used by both the live `appUrlOpen` listener and the
// cold-start `App.getLaunchUrl()` path.
export const handleDeepLink = (rawUrl) => {
  console.log('[DeepLink] received', rawUrl ? rawUrl.split('?')[0] : rawUrl);
  try {
    const url = new URL(rawUrl);

    // OAuth callback: slimmy://login-success?token=xxx&next=/app/dashboard
    if (url.protocol === 'slimmy:' && (url.hostname === 'login-success' || rawUrl.startsWith('slimmy://login-success'))) {
      const token = url.searchParams.get('token');
      const next = url.searchParams.get('next') || '/app/dashboard';
      console.log('[DeepLink] token received', !!token);
      console.log('[DeepLink] next', next.split('?')[0]);
      if (token) {
        console.log('[DeepLink] before session save');
        // Same storage read by lib/http.js for the Authorization header
        localStorage.setItem('session_token', token);
        console.log('[DeepLink] session saved');
        // Best-effort close of the in-app browser ("No active window to
        // close" is harmless — never surface it as an error)
        try { Browser.close().catch(() => {}); } catch (_) { /* noop */ }
        // SPA navigation — NO full page reload. AuthProvider reloads the
        // user and a Router-scoped listener navigates with React Router.
        console.log('[DeepLink] dispatching slimmy-auth-success, next =', next.split('?')[0]);
        window.dispatchEvent(new CustomEvent('slimmy-auth-success', { detail: { next } }));
      }
      return true;
    }

    if (url.hostname === 'slimmy.app') {
      // Universal link: navigate to the path within the app
      window.location.hash = '';
      window.history.pushState({}, '', url.pathname);
      window.dispatchEvent(new PopStateEvent('popstate'));
      return true;
    }
  } catch (e) {
    console.error('[DeepLink] error handling url:', e);
  }
  return false;
};

let deepLinkListenerRegistered = false;

export const registerDeepLinkListener = async () => {
  if (!isNative || deepLinkListenerRegistered) return;
  deepLinkListenerRegistered = true;
  try {
    console.log('[DeepLink] registering appUrlOpen listener');
    App.addListener('appUrlOpen', ({ url }) => handleDeepLink(url));
    console.log('[DeepLink] listener appUrlOpen registered');
  } catch (e) {
    console.error('[DeepLink] failed to register appUrlOpen listener:', e);
    deepLinkListenerRegistered = false;
    return;
  }
  // Cold start: the app may have been launched BY the deep link itself.
  // getLaunchUrl returns the SAME url on every call for the whole app
  // session — guard so the same OAuth is never processed twice (the
  // live appUrlOpen listener above keeps processing new events normally).
  try {
    const launchUrl = await App.getLaunchUrl();
    if (launchUrl?.url && launchUrl.url.startsWith('slimmy://login-success')) {
      const LAUNCH_HANDLED_KEY = 'slimmy_google_launch_handled';
      if (sessionStorage.getItem(LAUNCH_HANDLED_KEY) === '1') {
        console.log('[DeepLink] launch URL already handled - skipping');
      } else {
        sessionStorage.setItem(LAUNCH_HANDLED_KEY, '1');
        handleDeepLink(launchUrl.url);
      }
    }
  } catch (e) {
    console.warn('[DeepLink] getLaunchUrl not available:', e.message);
  }
};

// NOTE: no module-level auto-registration — the listener is registered
// explicitly from the app entrypoint (src/index.js) and, as a guarded
// backup, from initNative().

// ============================================================
// VIDEO RECORDING (Native camera for video)
// ============================================================

/**
 * Record a short video using native camera
 * @param {Object} options - {maxDuration: 10, quality: 'high'}
 * @returns {Promise<{dataUrl: string, blob: Blob, file: File, duration: number}>}
 */
export const recordVideo = async (options = {}) => {
  const maxDuration = options.maxDuration || 10; // Default 10 seconds
  
  console.log('[Video] recordVideo called, isNative:', isNative, 'maxDuration:', maxDuration);
  
  if (!isNative) {
    console.log('[Video] Not native, returning null - use HTML input');
    return null; // fallback to HTML input on web
  }

  try {
    const camera = await getCamera();
    const { CameraResultType, CameraSource } = await import('@capacitor/camera');

    console.log('[Video] Opening camera for video recording');
    
    // Note: Capacitor Camera plugin doesn't support video directly
    // We need to use a workaround with the file picker or native implementation
    // For now, return null to use HTML input on native too
    // TODO: Implement with @nicholasio/capacitor-native-video-recorder or similar
    
    return null;
  } catch (e) {
    console.log('[Video] Error or cancelled:', e.message);
    throw e;
  }
};

/**
 * Pick video from gallery
 * @returns {Promise<{dataUrl: string, blob: Blob, file: File}>}
 */
export const pickVideo = async () => {
  console.log('[Video] pickVideo called, isNative:', isNative);
  
  // For now, return null to use HTML file input on all platforms
  // This is the most reliable cross-platform approach
  return null;
};

/**
 * Compress video client-side (reduces file size)
 * Uses canvas-based compression for basic reduction
 * @param {File} videoFile - The video file to compress
 * @param {Object} options - {maxSizeMB: 25, maxDuration: 10}
 * @returns {Promise<{file: File, dataUrl: string, duration: number}>}
 */
export const compressVideo = async (videoFile, options = {}) => {
  const maxDuration = options.maxDuration || 10;
  const maxSizeMB = options.maxSizeMB || 25;
  
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    
    video.onloadedmetadata = () => {
      const duration = video.duration;
      
      // Check duration
      if (duration > maxDuration) {
        reject(new Error(`Video troppo lungo: ${Math.round(duration)}s (max ${maxDuration}s)`));
        return;
      }
      
      // Check file size
      const sizeMB = videoFile.size / (1024 * 1024);
      if (sizeMB > maxSizeMB * 2) { // Allow 2x for raw, will compress
        reject(new Error(`Video troppo grande: ${sizeMB.toFixed(1)}MB (max ${maxSizeMB * 2}MB)`));
        return;
      }
      
      // Read as data URL
      const reader = new FileReader();
      reader.onload = (e) => {
        resolve({
          file: videoFile,
          dataUrl: e.target.result,
          duration: Math.round(duration),
          sizeMB: sizeMB.toFixed(1)
        });
      };
      reader.onerror = () => reject(new Error('Errore lettura video'));
      reader.readAsDataURL(videoFile);
    };
    
    video.onerror = () => reject(new Error('Formato video non supportato'));
    video.src = URL.createObjectURL(videoFile);
  });
};

// ============================================================
// INITIALIZE ALL NATIVE FEATURES
// ============================================================

export const initNative = async () => {
  if (!isNative) return;

  try {
    // Set status bar style
    await statusBar.setDark();
  } catch (e) {
    console.warn('[Native] StatusBar not available:', e.message);
  }

  try {
    // Splash plugin removed — inline HTML splash handles visibility instead
  } catch (e) {
    console.warn('[Native] Splash not available:', e.message);
  }

  // Handle back button on Android
  if (isAndroid) {
    try {
      await appLifecycle.onBackButton(({ canGoBack }) => {
        if (canGoBack) {
          window.history.back();
        }
      });
    } catch (e) {
      console.warn('[Native] Back button handler not available:', e.message);
    }
  }

  // iOS: hide keyboard accessory bar globally (runtime call — works even
  // if the native capacitor.config.json is stale / not re-synced).
  if (isIOS) {
    try {
      const k = await getKeyboard();
      if (k) await k.setAccessoryBarVisible({ isVisible: false });
    } catch (e) {
      console.warn('[Native] Keyboard accessory bar:', e.message);
    }
  }

  // Deep links: listener already registered at module load (see
  // registerDeepLinkListener above) — safe re-invoke, guarded by flag.
  registerDeepLinkListener();
};
